import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiTrendingUp, FiTarget, FiLayers, FiCheckCircle, FiAlertTriangle,
  FiDollarSign, FiActivity, FiBarChart2, FiPieChart,
  FiBriefcase, FiUsers, FiShield, FiZap, FiGitBranch,
  FiChevronDown, FiChevronUp, FiDownload, FiInfo,
  FiGrid, FiList, FiFolder, FiGlobe, FiClock,
  FiCpu, FiDatabase, FiArrowRight, FiX, FiStar
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { generateCompleteTransformation } from '../../../utils/transformationEngine';

const TransformationView = () => {
  const { state, dispatch } = useSolution();
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState(new Set(['executiveSummary', 'financialImpact']));
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [showValueCalculator, setShowValueCalculator] = useState(false);
  
  // Derive requirements from current solution
  const requirements = useMemo(() => {
    const selectedComponents = state.selectedComponents;
    return {
      industry: deriveIndustry(selectedComponents),
      problemType: deriveProblemTypes(selectedComponents),
      dataType: deriveDataTypes(selectedComponents),
      scale: deriveScale(selectedComponents),
      budget: deriveBudget(state.totalCost),
      expertise: 'intermediate',
      deployment: deriveDeployment(selectedComponents),
      compliance: deriveCompliance(selectedComponents),
      timeframe: '12-months',
      priority: 'balanced'
    };
  }, [state.selectedComponents, state.totalCost]);
  
  const transformation = useMemo(() => {
    if (state.selectedComponents.length === 0) return null;
    
    const architecture = {
      components: state.selectedComponents,
      estimatedCost: state.totalCost,
      costModel: state.totalCost
    };
    
    return generateCompleteTransformation(architecture, requirements);
  }, [state.selectedComponents, state.totalCost, requirements]);
  
  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);
  
  const tabs = [
    { id: 'overview', name: 'Overview', icon: FiTarget },
    { id: 'financial', name: 'Financial Impact', icon: FiDollarSign },
    { id: 'roadmap', name: 'Roadmap', icon: FiGitBranch },
    { id: 'maturity', name: 'Maturity', icon: FiBarChart2 },
    { id: 'value', name: 'Value Engineering', icon: FiActivity },
    { id: 'operating', name: 'Operating Model', icon: FiBriefcase },
    { id: 'change', name: 'Change', icon: FiUsers },
    { id: 'risk', name: 'Risks', icon: FiShield },
    { id: 'kpi', name: 'KPIs', icon: FiTrendingUp },
    { id: 'benchmarks', name: 'Benchmarks', icon: FiGlobe }
  ];
  
  if (!transformation) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
          <div className="text-7xl mb-6">📊</div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Solution Architecture
          </h3>
          <p className="text-gray-500 mb-6">
            Generate or select components to unlock transformation strategy
          </p>
          <button
            onClick={() => dispatch({ type: 'SHOW_TOAST', payload: { type: 'info', message: 'Use Auto-Architect to generate a solution first' } })}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold"
          >
            Go to Auto-Architect
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3"
          >
            <FiTrendingUp className="w-10 h-10 text-blue-600" />
            Enterprise Transformation Strategy
          </motion.h2>
          <p className="text-lg text-gray-600">
            {transformation.executiveSummary.headline}
          </p>
        </div>
        
        {/* Executive Summary Hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-8 mb-8 text-white shadow-xl"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">${formatValue(transformation.financialImpact.totalAnnualValue)}</div>
              <div className="text-sm text-blue-100 mt-1">Annual Value</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{Math.round(transformation.financialImpact.roi)}%</div>
              <div className="text-sm text-blue-100 mt-1">Expected ROI</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{transformation.financialImpact.paybackPeriod} mo</div>
              <div className="text-sm text-blue-100 mt-1">Payback Period</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{transformation.executiveSummary.confidenceLevel}%</div>
              <div className="text-sm text-blue-100 mt-1">Confidence</div>
            </div>
          </div>
        </motion.div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 sticky top-0 z-10 bg-gray-50/80 backdrop-blur-lg rounded-xl p-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Strategic Objectives */}
                <SectionCard
                  title="🎯 Strategic Objectives"
                  expanded={expandedSections.has('objectives')}
                  onToggle={() => toggleSection('objectives')}
                >
                  <div className="space-y-3">
                    {transformation.businessTransformation.strategicObjectives.map((obj, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
                        <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-gray-800 font-medium">{obj}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                
                {/* Value Propositions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {transformation.businessTransformation.valuePropositions.map((vp, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all"
                    >
                      <h4 className="font-bold text-gray-900 mb-2">{vp.proposition}</h4>
                      <p className="text-sm text-gray-600 mb-4">{vp.description}</p>
                      <div className="text-3xl font-bold text-green-600">{vp.expectedImpact}</div>
                      <div className="text-xs text-gray-500 mt-2">Within {vp.timeframe}</div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Priority Use Cases */}
                <SectionCard
                  title="⚡ Priority Use Cases"
                  expanded={expandedSections.has('useCases')}
                  onToggle={() => toggleSection('useCases')}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {transformation.industryData.priorityUseCases.map((useCase, i) => (
                      <div
                        key={i}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedUseCase === useCase
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedUseCase(selectedUseCase === useCase ? null : useCase)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{['🎯', '⚡', '💡', '🔮', '🚀'][i]}</span>
                          <span className="font-semibold text-gray-900">{useCase}</span>
                          {selectedUseCase === useCase && (
                            <FiCheckCircle className="w-5 h-5 text-blue-500 ml-auto" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                
                {/* Quick Wins */}
                <SectionCard
                  title="🚀 Quick Wins"
                  expanded={expandedSections.has('quickWins')}
                  onToggle={() => toggleSection('quickWins')}
                >
                  <div className="space-y-3">
                    {transformation.quickWins.map((win, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{win.initiative}</div>
                          <div className="text-sm text-gray-600">{win.impact}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-green-600">{win.timeline}</div>
                          <div className="text-xs text-gray-500">Effort: {win.effort}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            )}
            
            {/* FINANCIAL TAB */}
            {activeTab === 'financial' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total Annual Value" value={`$${formatValue(transformation.financialImpact.totalAnnualValue)}`} color="green" />
                  <StatCard label="Investment" value={`$${formatValue(transformation.financialImpact.totalInvestment)}`} color="blue" />
                  <StatCard label="ROI" value={`${Math.round(transformation.financialImpact.roi)}%`} color="purple" />
                  <StatCard label="IRR" value={transformation.financialImpact.irr} color="yellow" />
                </div>
                
                {/* Value Breakdown */}
                <SectionCard title="💰 Value Breakdown" expanded={true} onToggle={() => {}}>
                  <div className="space-y-4">
                    {Object.entries(transformation.financialImpact.valueBreakdown).map(([key, value], i) => (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-medium text-gray-700 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="font-bold text-gray-900">${formatValue(value)}</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              ['bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-yellow-500', 'bg-pink-500'][i]
                            }`}
                            style={{ width: `${(value / transformation.financialImpact.totalAnnualValue) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                
                {/* EBITDA Details */}
                <SectionCard title="📈 EBITDA Impact" expanded={expandedSections.has('ebitda')} onToggle={() => toggleSection('ebitda')}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm text-gray-500">Incremental EBITDA</div>
                      <div className="text-2xl font-bold text-green-600">
                        ${formatValue(transformation.financialImpact.ebitdaDetails?.incrementalEBITDA || 0)}
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm text-gray-500">NPV (3-year)</div>
                      <div className="text-2xl font-bold text-blue-600">
                        ${formatValue(transformation.financialImpact.npv || 0)}
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </div>
            )}
            
            {/* ROADMAP TAB */}
            {activeTab === 'roadmap' && (
              <div className="space-y-6">
                {transformation.digitalRoadmap.phases.map((phase, i) => (
                  <motion.div
                    key={phase.phase}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">
                        <span className="text-blue-600">Phase {phase.phase}:</span> {phase.name}
                      </h3>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                          {phase.duration}
                        </span>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {phase.budget}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">{phase.focus}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Activities</h4>
                        <ul className="space-y-2">
                          {phase.activities.map((activity, j) => (
                            <li key={j} className="flex items-center gap-2 text-sm text-gray-700">
                              <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              {activity}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Deliverables</h4>
                        <ul className="space-y-2">
                          {phase.deliverables.map((deliverable, j) => (
                            <li key={j} className="flex items-center gap-2 text-sm text-gray-700">
                              <FiCheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
            
            {/* MATURITY TAB */}
            {activeTab === 'maturity' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900">Capability Maturity Assessment</h3>
                    <span className="text-sm text-gray-500">
                      Baseline: {transformation.maturityAssessment.baselineMaturity} → Target: {transformation.maturityAssessment.targetMaturity}
                    </span>
                  </div>
                  <div className="space-y-6">
                    {transformation.maturityAssessment.dimensions.map((dim, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-semibold text-gray-700">{dim.dimension}</span>
                          <span className="text-gray-500">
                            Level {dim.currentLevel} / {dim.targetLevel}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                            style={{ width: `${(dim.currentLevel / dim.targetLevel) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* VALUE ENGINEERING TAB */}
            {activeTab === 'value' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(transformation.valueEngineering).map(([key, value], i) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
                  >
                    <h3 className="text-lg font-bold text-gray-900 capitalize mb-4">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    
                    {key === 'ebitdaImpact' ? (
                      <div className="space-y-3">
                        {Object.entries(value).map(([subKey, subValue]) => (
                          <div key={subKey} className="p-4 bg-gray-50 rounded-xl">
                            <div className="font-semibold text-gray-900 capitalize">
                              {subKey.replace(/([A-Z])/g, ' $1').trim()}
                            </div>
                            <div className="text-2xl font-bold text-green-600 mt-1">
                              ${formatValue(subValue.annualValue || 0)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{subValue.expectedRange}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm text-gray-600 mb-2">{value.description || value.currentState}</div>
                        <div className="text-2xl font-bold text-blue-600">
                          ${formatValue(value.annualValue || 0)}
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          Expected: {value.expectedImprovement}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
            

{activeTab === 'operating' && transformation?.operatingModel && (
  <div className="space-y-6">
    {/* Governance Structure Header */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">
            {transformation.operatingModel.governanceStructure.structureName}
          </h3>
          <p className="text-blue-100 mt-1">
            {transformation.operatingModel.governanceStructure.description}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-blue-100">Team Size</div>
          <div className="text-2xl font-bold">
            {transformation.operatingModel.governanceStructure.deliveryTeams.teamSize}
          </div>
        </div>
      </div>
      <div className="mt-4 p-3 bg-white/10 rounded-lg text-sm">
        <strong>Rationale:</strong> {transformation.operatingModel.governanceStructure.rationale}
      </div>
    </motion.div>

    {/* Executive Sponsorship */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">👔 Executive Sponsorship</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-blue-50 rounded-xl">
          <div className="text-sm text-gray-500">Role</div>
          <div className="font-bold text-gray-900">
            {transformation.operatingModel.governanceStructure.executiveSponsorship.role}
          </div>
        </div>
        <div className="p-4 bg-blue-50 rounded-xl">
          <div className="text-sm text-gray-500">Meeting Cadence</div>
          <div className="font-bold text-gray-900">
            {transformation.operatingModel.governanceStructure.executiveSponsorship.meetingCadence}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <h4 className="font-semibold text-gray-700 mb-2">Responsibilities</h4>
        <ul className="space-y-2">
          {transformation.operatingModel.governanceStructure.executiveSponsorship.responsibilities.map((resp, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
              <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              {resp}
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Steering Committee */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🎯 Steering Committee</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {transformation.operatingModel.governanceStructure.steeringCommittee.members.map((member, i) => (
          <span key={i} className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
            {member}
          </span>
        ))}
      </div>
      <div className="text-sm text-gray-600">
        <strong>Meeting Cadence:</strong> {transformation.operatingModel.governanceStructure.steeringCommittee.meetingCadence}
      </div>
    </div>

    {/* Delivery Teams & Required Roles */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">👥 Delivery Teams & Required Roles</h3>
      <div className="mb-4">
        <div className="text-sm text-gray-500">Structure</div>
        <div className="font-semibold text-gray-900">
          {transformation.operatingModel.governanceStructure.deliveryTeams.structure}
        </div>
      </div>
      <div className="mb-4">
        <div className="text-sm text-gray-500">Methodology</div>
        <div className="font-semibold text-gray-900">
          {transformation.operatingModel.governanceStructure.deliveryTeams.methodology}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-gray-700 mb-3">Required Roles (Derived from Selected Components)</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {transformation.operatingModel.governanceStructure.deliveryTeams.roles.map((role, i) => (
            <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm font-medium text-gray-800">
              {role}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Skill Requirements */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🔧 Required Skills</h3>
      <div className="flex flex-wrap gap-2">
        {transformation.operatingModel.contextSpecific?.skillRequirements?.map((skill, i) => (
          <span key={i} className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            {skill}
          </span>
        ))}
      </div>
    </div>

    {/* Team Allocation */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Team Allocation</h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-500">Total Components</div>
          <div className="text-2xl font-bold text-gray-900">
            {transformation.operatingModel.contextSpecific?.teamAllocation?.totalComponents || 0}
          </div>
        </div>
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-500">Suggested Team Size</div>
          <div className="text-2xl font-bold text-gray-900">
            {transformation.operatingModel.contextSpecific?.teamAllocation?.suggestedTeamSize}
          </div>
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-gray-700 mb-2">Layer Distribution</h4>
        <div className="space-y-2">
          {Object.entries(transformation.operatingModel.contextSpecific?.teamAllocation?.layerDistribution || {}).map(([layer, count], i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{layer}</span>
              <span className="font-semibold text-gray-900">{count} components</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Decision Rights */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">⚖️ Decision Rights</h3>
      <div className="space-y-3">
        {Object.entries(transformation.operatingModel.decisionRights || {}).map(([decision, rights], i) => (
          <div key={i} className="p-4 bg-gray-50 rounded-xl">
            <div className="font-semibold text-gray-900 mb-2">{decision}</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Approver:</span>
                <span className="text-gray-800 font-medium">{rights.approver}</span>
              </div>
              <div>
                <span className="text-gray-500">Consultation:</span>
                <span className="text-gray-800 font-medium">{rights.consultation}</span>
              </div>
              <div>
                <span className="text-gray-500">Informed:</span>
                <span className="text-gray-800 font-medium">{rights.informed}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)}


{activeTab === 'change' && transformation?.changeManagement && (
  <div className="space-y-6">
    {/* Change Complexity Overview */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Change Management Strategy</h3>
          <p className="text-purple-100 mt-1">
            Complexity: {transformation.changeManagement.contextSpecific?.changeComplexity}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-purple-100">Adoption Strategy</div>
          <div className="text-sm font-semibold">
            {transformation.changeManagement.contextSpecific?.adoptionStrategy}
          </div>
        </div>
      </div>
    </motion.div>

    {/* Affected Teams */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🏢 Affected Teams</h3>
      <div className="flex flex-wrap gap-2">
        {transformation.changeManagement.contextSpecific?.affectedTeams?.map((team, i) => (
          <span key={i} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            {team}
          </span>
        ))}
      </div>
    </div>

    {/* Stakeholder Engagement */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">👥 Stakeholder Engagement</h3>
      <div className="space-y-4">
        {transformation.changeManagement.stakeholderGroups?.map((stakeholder, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="p-4 bg-gray-50 rounded-xl"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-gray-900">{stakeholder.group}</h4>
              <span className="text-xs text-gray-500">Champion: {stakeholder.champion}</span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{stakeholder.engagementStrategy}</p>
            <div className="flex flex-wrap gap-2">
              {stakeholder.keyMessages?.map((msg, j) => (
                <span key={j} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                  {msg}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>

    {/* Training Needs */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">📚 Training Needs (Derived from Solution)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {transformation.changeManagement.contextSpecific?.trainingNeeds?.map((need, i) => (
          <div key={i} className="p-4 bg-green-50 rounded-xl flex items-center gap-3">
            <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="font-medium text-gray-800">{need}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Training Programs */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🎓 Training Programs</h3>
      <div className="space-y-3">
        {transformation.changeManagement.trainingPrograms?.map((training, i) => (
          <div key={i} className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-gray-900">{training.program}</h4>
              <span className="text-sm text-gray-500">{training.duration}</span>
            </div>
            <div className="text-sm text-gray-600 mb-2">Audience: {training.audience}</div>
            <div className="text-sm text-gray-600 mb-2">Mode: {training.mode}</div>
            <div className="flex flex-wrap gap-2">
              {training.topics?.map((topic, j) => (
                <span key={j} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Change Timeline */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">⏱️ Change Timeline</h3>
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(transformation.changeManagement.contextSpecific?.changeTimeline || {}).map(([phase, duration], i) => (
          <div key={i} className="p-4 bg-gray-50 rounded-xl text-center">
            <div className="text-sm text-gray-500 capitalize">{phase}</div>
            <div className="text-xl font-bold text-blue-600">{duration}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Success Factors */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">✅ Success Factors</h3>
      <div className="space-y-2">
        {transformation.changeManagement.contextSpecific?.successFactors?.map((factor, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-gray-800 font-medium">{factor}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Resistance Management */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🛡️ Resistance Management</h3>
      <div className="space-y-4">
        {transformation.changeManagement.resistanceManagement?.map((resistance, i) => (
          <div key={i} className="p-4 bg-orange-50 rounded-xl">
            <div className="font-bold text-gray-900 mb-2">{resistance.type}</div>
            <div className="text-sm text-gray-600 mb-2">Strategy: {resistance.strategy}</div>
            <div className="flex flex-wrap gap-2">
              {resistance.tactics?.map((tactic, j) => (
                <span key={j} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                  {tactic}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)}

            {/* RISK TAB */}
            {activeTab === 'risk' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total Risks" value={transformation.riskManagement.totalRisks} color="gray" />
                  <StatCard label="Critical" value={transformation.riskManagement.criticalRisks} color="red" />
                  <StatCard label="High" value={transformation.riskManagement.highRisks} color="orange" />
                  <StatCard label="Medium/Low" value={transformation.riskManagement.mediumRisks + transformation.riskManagement.lowRisks} color="yellow" />
                </div>
                
                <div className="space-y-4">
                  {transformation.riskManagement.risks.map((risk, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`bg-white rounded-xl shadow-sm border p-4 ${
                        risk.riskLevel === 'Critical' ? 'border-red-300 bg-red-50' :
                        risk.riskLevel === 'High' ? 'border-orange-300 bg-orange-50' :
                        'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">{risk.risk}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          risk.riskLevel === 'Critical' ? 'bg-red-100 text-red-700' :
                          risk.riskLevel === 'High' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {risk.riskLevel}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Impact: {risk.impact} | Probability: {risk.probability}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <strong>Mitigation:</strong> {risk.mitigation}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
            {/* KPI TAB */}
            {activeTab === 'kpi' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(transformation.kpiFramework).filter(([key]) => key !== 'industrySpecific').map(([category, kpis], i) => (
                  <motion.div
                    key={category}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
                  >
                    <h3 className="text-lg font-bold text-gray-900 capitalize mb-4">{category}</h3>
                    <div className="space-y-3">
                      {kpis.map((kpi, j) => (
                        <div key={j} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-semibold text-gray-800">{kpi.kpi || kpi.metric}</div>
                            <div className="text-xs text-gray-500">{kpi.description || kpi.frequency}</div>
                          </div>
                          <span className="font-bold text-blue-600">{kpi.target}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
            
            {/* BENCHMARKS TAB */}
            {activeTab === 'benchmarks' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Industry Benchmarks</h3>
                  <div className="space-y-4">
                    {Object.entries(transformation.benchmarks.industryBenchmarks.performanceMetrics || {}).map(([metric, data], i) => (
                      <div key={metric}>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-medium text-gray-700 capitalize">{metric.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="text-gray-500">Industry: {data.industry} | Best: {data.best}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                            style={{ width: `${(parseFloat(data.industry) / parseFloat(data.best)) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ==================== HELPER COMPONENTS ====================

const SectionCard = ({ title, expanded, onToggle, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
    >
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      {expanded ? <FiChevronUp className="w-5 h-5 text-gray-500" /> : <FiChevronDown className="w-5 h-5 text-gray-500" />}
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="overflow-hidden"
        >
          <div className="p-5 border-t border-gray-100">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const StatCard = ({ label, value, color }) => {
  const colorClasses = {
    green: 'from-green-500 to-emerald-500',
    blue: 'from-blue-500 to-indigo-500',
    purple: 'from-purple-500 to-pink-500',
    yellow: 'from-yellow-500 to-orange-500',
    red: 'from-red-500 to-pink-500',
    orange: 'from-orange-500 to-red-500',
    gray: 'from-gray-500 to-gray-600'
  };
  
  return (
    <div className={`bg-gradient-to-br ${colorClasses[color] || colorClasses.blue} rounded-2xl p-6 text-white shadow-lg`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-sm opacity-90 mt-1">{label}</div>
    </div>
  );
};

const formatValue = (value) => {
  if (!value) return '0';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return `${Math.round(value)}`;
};

// ==================== DERIVATION HELPERS ====================

const deriveIndustry = (components) => {
  const tags = components.flatMap(c => c.tags || []);
  if (tags.some(t => ['medical', 'healthcare', 'hipaa'].includes(t))) return 'healthcare';
  if (tags.some(t => ['finance', 'banking', 'trading'].includes(t))) return 'finance';
  if (tags.some(t => ['ecommerce', 'retail'].includes(t))) return 'ecommerce';
  if (tags.some(t => ['manufacturing', 'industrial'].includes(t))) return 'manufacturing';
  if (tags.some(t => ['autonomous', 'robotics'].includes(t))) return 'autonomous';
  if (tags.some(t => ['chatbot', 'customer-service'].includes(t))) return 'customer-service';
  return 'general';
};

const deriveProblemTypes = (components) => {
  const types = [];
  const tags = components.flatMap(c => c.tags || []);
  if (tags.some(t => ['classification'].includes(t))) types.push('classification');
  if (tags.some(t => ['vision', 'image', 'cnn'].includes(t))) types.push('vision');
  if (tags.some(t => ['nlp', 'text', 'transformer'].includes(t))) types.push('nlp');
  if (tags.some(t => ['generation', 'gan', 'diffusion'].includes(t))) types.push('generation');
  if (tags.some(t => ['recommendation'].includes(t))) types.push('recommendation');
  if (tags.some(t => ['anomaly', 'fraud'].includes(t))) types.push('anomaly');
  if (tags.some(t => ['chatbot', 'conversational'].includes(t))) types.push('chatbot');
  return types.length > 0 ? types : ['classification'];
};

const deriveDataTypes = (components) => {
  const types = [];
  const tags = components.flatMap(c => c.tags || []);
  if (tags.some(t => ['structured', 'sql', 'relational'].includes(t))) types.push('structured');
  if (tags.some(t => ['image', 'vision'].includes(t))) types.push('image');
  if (tags.some(t => ['text', 'nlp'].includes(t))) types.push('text');
  if (tags.some(t => ['sensor', 'iot'].includes(t))) types.push('sensor');
  if (tags.some(t => ['time-series', 'temporal'].includes(t))) types.push('time-series');
  return types.length > 0 ? types : ['structured'];
};

const deriveScale = (components) => {
  const count = components.length;
  if (count > 30) return 'enterprise';
  if (count > 15) return 'smb';
  return 'startup';
};

const deriveBudget = (totalCost) => {
  const high = totalCost?.high || 0;
  if (high > 100000) return 'enterprise';
  if (high > 10000) return 'high';
  if (high > 1000) return 'medium';
  return 'low';
};

const deriveDeployment = (components) => {
  const tags = components.flatMap(c => c.tags || []);
  if (tags.some(t => ['edge', 'mobile', 'on-device'].includes(t))) return 'edge';
  if (tags.some(t => ['on-premise'].includes(t))) return 'on-premise';
  if (tags.some(t => ['hybrid'].includes(t))) return 'hybrid';
  return 'cloud';
};

const deriveCompliance = (components) => {
  const tags = components.flatMap(c => c.tags || []);
  if (tags.some(t => ['hipaa', 'compliance', 'audit'].includes(t))) return 'high';
  if (tags.some(t => ['security', 'encryption'].includes(t))) return 'medium';
  return 'low';
};

export default TransformationView;